import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import TermsConditionsContent from "./TermsConditionsContent";

import "./TermsConditionsModal.css";

export default function TermsConditionsModal({ open, onClose }) {
  const { t } = useTranslation("home");

  const modalRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    requestAnimationFrame(() => {
      modalRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="legup-terms-modal__overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <section
        ref={modalRef}
        className="legup-terms-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="legup-terms-modal-title"
        tabIndex={-1}
      >
        <header className="legup-terms-modal__header">
          <div className="legup-terms-modal__heading">
            <span className="legup-terms-modal__eyebrow">
              {t("legalPages.terms.kicker", "LEGAL INFORMATION")}
            </span>

            <h2 id="legup-terms-modal-title">
              {t("legalPages.terms.title", "Terms & Conditions")}
            </h2>

            <p>
              {t(
                "legalPages.terms.description",
                "Terms governing access to and use of the LEG-UP platform.",
              )}
            </p>
          </div>

          <button
            type="button"
            className="legup-terms-modal__close"
            onClick={onClose}
            aria-label={t("common:close", "Close")}
          >
            <X size={21} strokeWidth={2} />
          </button>
        </header>

        <div className="legup-terms-modal__body">
          <TermsConditionsContent />
        </div>

        <footer className="legup-terms-modal__footer">
          <button
            type="button"
            className="legup-terms-modal__done"
            onClick={onClose}
          >
            {t("common:close", "Close")}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
